import { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useEntity } from '../../contexts/EntityContext';
import { useAuth } from '../../contexts/AuthContext';
import { api, WalletMovement, Wallet } from '../../lib/api';
import { formatAmount } from '../../lib/format';
import { LoadingState, EmptyState } from '../../components/ui/Spinner';
import { ConfirmModal } from '../../components/ui/Modal';
import { Link, useSearchParams } from 'react-router-dom';

function formatDate(d: string) {
  return new Date(d).toLocaleDateString('en-GB', {
    day: '2-digit', month: '2-digit', year: 'numeric',
  });
}

function formatTime(d: string) {
  return new Date(d).toLocaleTimeString('en-GB', {
    hour: '2-digit', minute: '2-digit',
  });
}

function txTypeLabel(type: string | null, isDebt: boolean) {
  if (isDebt) return 'Debt Payment';
  if (!type) return '–';
  const map: Record<string, string> = {
    exchange: 'Exchange',
    deposit: 'Deposit',
    withdrawal: 'Withdrawal',
    expense: 'Expense',
    transfer: 'Transfer',
  };
  return map[type] || type;
}

// Convert ISO string to datetime-local value (YYYY-MM-DDTHH:mm)
function toDatetimeLocal(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default function CashierMovementsPage() {
  const { t } = useTranslation();
  const { currentEntity } = useEntity();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const isAdmin = user?.role === 'admin';

  const today = new Date().toLocaleDateString('en-CA'); // YYYY-MM-DD in local timezone
  const [searchParams, setSearchParams] = useSearchParams();

  const startDate = searchParams.get('startDate') || '';
  const endDate = searchParams.get('endDate') || '';
  const isTodayActive = startDate === today && endDate === today;
  const myOnly = searchParams.get('myOnly') === 'true';
  const summaryMode = (searchParams.get('summaryMode') as 'wallets' | 'currencies') || 'wallets';
  const directionFilter = (searchParams.get('direction') as 'all' | 'in' | 'out') || 'all';
  const walletId = searchParams.get('walletId') || undefined;
  const walletNameFromUrl = searchParams.get('walletName') || undefined;
  const transactionId = searchParams.get('transactionId') || undefined;

  const setUrlParam = useCallback((key: string, value: string, defaultValue = '') => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (value === defaultValue) next.delete(key); else next.set(key, value);
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const setStartDate = useCallback((v: string) => setUrlParam('startDate', v), [setUrlParam]);
  const setEndDate = useCallback((v: string) => setUrlParam('endDate', v), [setUrlParam]);
  const setMyOnly = useCallback((v: boolean) => setUrlParam('myOnly', v ? 'true' : '', ''), [setUrlParam]);
  const setSummaryMode = useCallback((v: 'wallets' | 'currencies') => setUrlParam('summaryMode', v, 'wallets'), [setUrlParam]);
  const setDirectionFilter = useCallback((v: 'all' | 'in' | 'out') => setUrlParam('direction', v, 'all'), [setUrlParam]);

  // Edit modal state
  const [editingMovement, setEditingMovement] = useState<WalletMovement | null>(null);
  const [editAmount, setEditAmount] = useState('');
  const [editCreatedAt, setEditCreatedAt] = useState('');
  const [editTxId, setEditTxId] = useState('');
  const [editWalletId, setEditWalletId] = useState('');
  const [editCurrency, setEditCurrency] = useState('');
  const [editIsDebtPayment, setEditIsDebtPayment] = useState(false);
  const [editError, setEditError] = useState('');

  // Delete confirmation state
  const [deletingMovementId, setDeletingMovementId] = useState<string | null>(null);

  // Create modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createWalletId, setCreateWalletId] = useState('');
  const [createAmount, setCreateAmount] = useState('');
  const [createCurrency, setCreateCurrency] = useState('');
  const [createDirection, setCreateDirection] = useState<'in' | 'out'>('in');
  const [createTxId, setCreateTxId] = useState('');
  const [createError, setCreateError] = useState('');

  const updateMutation = useMutation({
    mutationFn: (vars: { id: string; amount?: number; createdAt?: string; transactionId?: string | null; walletId?: string; currency?: string; isDebtPayment?: boolean }) =>
      api.movements.update(currentEntity!.id, vars.id, {
        amount: vars.amount,
        createdAt: vars.createdAt,
        transactionId: vars.transactionId,
        walletId: vars.walletId,
        currency: vars.currency,
        isDebtPayment: vars.isDebtPayment,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['movements'] });
      setEditingMovement(null);
    },
    onError: (err: Error) => {
      setEditError(err.message || 'Ошибка сохранения');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (movementId: string) =>
      api.movements.delete(currentEntity!.id, movementId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['movements'] });
      setDeletingMovementId(null);
    },
  });

  const createMutation = useMutation({
    mutationFn: (vars: { walletId: string; amount: number; currency: string; direction: 'in' | 'out'; transactionId?: string | null }) =>
      api.movements.create(currentEntity!.id, vars),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['movements'] });
      setShowCreateModal(false);
    },
    onError: (err: Error) => {
      setCreateError(err.message || 'Ошибка создания');
    },
  });

  // Wallets list for create/edit modals
  const { data: walletsData } = useQuery({
    queryKey: ['admin-wallets', currentEntity?.id],
    queryFn: () => api.admin.wallets.list(currentEntity!.id),
    enabled: !!currentEntity,
  });
  const wallets: Wallet[] = walletsData?.data ?? [];

  function openEdit(row: WalletMovement) {
    setEditingMovement(row);
    setEditAmount(String(parseFloat(row.amount)));
    setEditCreatedAt(row.createdAt ? toDatetimeLocal(row.createdAt) : '');
    setEditTxId(row.transactionId || '');
    setEditWalletId(row.walletId || '');
    setEditCurrency(row.currency || '');
    setEditIsDebtPayment(row.isDebtPayment || false);
    setEditError('');
  }

  function handleEditSave() {
    if (!editingMovement) return;
    const parsedAmount = parseFloat(editAmount);
    if (isNaN(parsedAmount)) {
      setEditError('Введите корректную сумму');
      return;
    }
    let isoCreatedAt: string | undefined;
    if (editCreatedAt) {
      isoCreatedAt = new Date(editCreatedAt).toISOString();
    }
    // Determine if transactionId changed (admin only)
    let txIdUpdate: string | null | undefined = undefined;
    if (isAdmin) {
      const origTxId = editingMovement.transactionId || '';
      if (editTxId !== origTxId) {
        txIdUpdate = editTxId.trim() || null; // empty string -> null (unlink)
      }
    }
    // Wallet change
    const walletIdUpdate = editWalletId !== (editingMovement.walletId || '') ? editWalletId : undefined;
    // Currency change
    const currencyUpdate = editCurrency !== (editingMovement.currency || '') ? editCurrency : undefined;
    // Debt payment change
    const debtUpdate = editIsDebtPayment !== (editingMovement.isDebtPayment || false) ? editIsDebtPayment : undefined;

    updateMutation.mutate({
      id: editingMovement.id,
      amount: parsedAmount,
      createdAt: isoCreatedAt,
      transactionId: txIdUpdate,
      walletId: walletIdUpdate,
      currency: currencyUpdate,
      isDebtPayment: debtUpdate,
    });
  }

  function openCreate() {
    setCreateWalletId('');
    setCreateAmount('');
    setCreateCurrency('');
    setCreateDirection('in');
    setCreateTxId('');
    setCreateError('');
    setShowCreateModal(true);
  }

  function handleCreateSave() {
    const parsedAmount = parseFloat(createAmount);
    if (!createWalletId) { setCreateError('Выберите кошелёк'); return; }
    if (!createCurrency) { setCreateError('Укажите валюту'); return; }
    if (isNaN(parsedAmount) || parsedAmount <= 0) { setCreateError('Введите корректную сумму'); return; }
    createMutation.mutate({
      walletId: createWalletId,
      amount: parsedAmount,
      currency: createCurrency.toUpperCase(),
      direction: createDirection,
      transactionId: createTxId.trim() || null,
    });
  }

  const { data, isLoading, error } = useQuery({
    queryKey: ['movements', currentEntity?.id, startDate, endDate, myOnly, walletId, transactionId],
    queryFn: () => api.movements.list(currentEntity!.id, { startDate, endDate, myOnly, walletId, transactionId }),
    enabled: !!currentEntity,
  });

  if (!currentEntity) return null;

  const allRows = data?.data ?? [];
  const rows = directionFilter === 'all'
    ? allRows
    : allRows.filter((r) => {
        const amt = parseFloat(r.amount);
        return directionFilter === 'in' ? amt >= 0 : amt < 0;
      });

  // Group by currency for summary
  const currencySummary: Record<string, { inflow: number; outflow: number }> = {};
  for (const r of rows) {
    const amt = parseFloat(r.amount);
    if (!currencySummary[r.currency]) currencySummary[r.currency] = { inflow: 0, outflow: 0 };
    if (amt >= 0) currencySummary[r.currency].inflow += amt;
    else currencySummary[r.currency].outflow += Math.abs(amt);
  }

  // Group by wallet for summary
  const walletSummary: Record<string, { walletName: string; currency: string; inflow: number; outflow: number }> = {};
  for (const r of rows) {
    const amt = parseFloat(r.amount);
    if (!walletSummary[r.walletId]) walletSummary[r.walletId] = { walletName: r.walletName, currency: r.currency, inflow: 0, outflow: 0 };
    if (amt >= 0) walletSummary[r.walletId].inflow += amt;
    else walletSummary[r.walletId].outflow += Math.abs(amt);
  }

  return (
    <div className="movements-page">
      {/* Header */}
      <div className="movements-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <h1 className="movements-title">Cash Movements</h1>
          {isAdmin && (
            <button className="movements-create-btn" onClick={openCreate}>
              + Создать
            </button>
          )}
        </div>
        <div className="movements-date-filter">
          <input
            type="date"
            lang="ru"
            className="movements-date-input"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            title="Дата от (локальное время)"
          />
          <span className="movements-date-sep">→</span>
          <input
            type="date"
            lang="ru"
            className="movements-date-input"
            value={endDate}
            min={startDate}
            onChange={(e) => setEndDate(e.target.value)}
            title="Дата по (локальное время)"
          />
          <button
            className={`movements-today-btn${isTodayActive ? ' active' : ''}`}
            onClick={() => {
              setSearchParams(prev => {
                const next = new URLSearchParams(prev);
                if (isTodayActive) {
                  next.delete('startDate');
                  next.delete('endDate');
                } else {
                  next.set('startDate', today);
                  next.set('endDate', today);
                }
                return next;
              }, { replace: true });
            }}
            title="Сегодня в локальном времени"
          >
            Сегодня
          </button>
          <button
            id="movements-my-only-toggle"
            className={`movements-my-only-btn${myOnly ? ' active' : ''}`}
            onClick={() => setMyOnly(!myOnly)}
            title={myOnly ? 'Показаны операции только текущего кассира' : 'Показаны операции всех сотрудников'}
          >
            {myOnly ? 'Только мои' : 'Все сотр.'}
          </button>
          <div className="movements-direction-filter">
            <button
              className={`movements-direction-btn${directionFilter === 'all' ? ' active' : ''}`}
              onClick={() => setDirectionFilter('all')}
            >
              Все
            </button>
            <button
              className={`movements-direction-btn in${directionFilter === 'in' ? ' active' : ''}`}
              onClick={() => setDirectionFilter('in')}
            >
              + Приход
            </button>
            <button
              className={`movements-direction-btn out${directionFilter === 'out' ? ' active' : ''}`}
              onClick={() => setDirectionFilter('out')}
            >
              − Расход
            </button>
          </div>
          {walletId && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '4px 10px', background: 'var(--color-primary)', color: 'white', borderRadius: '6px', fontSize: '12px', fontWeight: 600 }}>
              📂 {walletNameFromUrl || 'Wallet'}
              <button
                onClick={() => {
                  const newParams = new URLSearchParams(searchParams);
                  newParams.delete('walletId');
                  newParams.delete('walletName');
                  setSearchParams(newParams);
                }}
                style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer', padding: '0 2px', fontSize: '14px', lineHeight: 1 }}
                title="Сбросить фильтр кошелька"
              >
                ✕
              </button>
            </span>
          )}
          {transactionId && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '4px 10px', background: 'var(--ctp-teal, #14b8a6)', color: 'white', borderRadius: '6px', fontSize: '12px', fontWeight: 600 }}>
              🔗 TX #{transactionId.slice(0, 8)}
              <button
                onClick={() => {
                  const newParams = new URLSearchParams(searchParams);
                  newParams.delete('transactionId');
                  setSearchParams(newParams);
                }}
                style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer', padding: '0 2px', fontSize: '14px', lineHeight: 1 }}
                title="Сбросить фильтр транзакции"
              >
                ✕
              </button>
            </span>
          )}
        </div>
      </div>

      {/* Summary cards */}
      {rows.length > 0 && (
        <>
          <div className="movements-summary-toggle">
            <button
              className={`movements-summary-toggle-btn${summaryMode === 'wallets' ? ' active' : ''}`}
              onClick={() => setSummaryMode('wallets')}
            >
              По кошелькам
            </button>
            <button
              className={`movements-summary-toggle-btn${summaryMode === 'currencies' ? ' active' : ''}`}
              onClick={() => setSummaryMode('currencies')}
            >
              По валютам
            </button>
          </div>
          <div className="movements-summary">
            {summaryMode === 'currencies'
              ? Object.entries(currencySummary).map(([cur, { inflow, outflow }]) => (
                  <div key={cur} className="movements-summary-card">
                    <div className="movements-summary-currency">{cur}</div>
                    <div className="movements-summary-row">
                      <span className="movements-summary-label">In</span>
                      <span className="movements-amount-in">+{formatAmount(inflow.toFixed(8), cur)}</span>
                    </div>
                    <div className="movements-summary-row">
                      <span className="movements-summary-label">Out</span>
                      <span className="movements-amount-out">–{formatAmount(outflow.toFixed(8), cur)}</span>
                    </div>
                    <div className="movements-summary-divider" />
                    <div className="movements-summary-row">
                      <span className="movements-summary-label">Net</span>
                      <span className={inflow - outflow >= 0 ? 'movements-amount-in' : 'movements-amount-out'}>
                        {inflow - outflow >= 0 ? '+' : '–'}
                        {formatAmount(Math.abs(inflow - outflow).toFixed(8), cur)}
                      </span>
                    </div>
                  </div>
                ))
              : Object.entries(walletSummary).map(([wId, { walletName, currency, inflow, outflow }]) => (
                  <div key={wId} className="movements-summary-card">
                    <div className="movements-summary-currency">{walletName}</div>
                    <div className="movements-summary-row">
                      <span className="movements-summary-label">In</span>
                      <span className="movements-amount-in">+{formatAmount(inflow.toFixed(8), currency)}</span>
                    </div>
                    <div className="movements-summary-row">
                      <span className="movements-summary-label">Out</span>
                      <span className="movements-amount-out">–{formatAmount(outflow.toFixed(8), currency)}</span>
                    </div>
                    <div className="movements-summary-divider" />
                    <div className="movements-summary-row">
                      <span className="movements-summary-label">Net</span>
                      <span className={inflow - outflow >= 0 ? 'movements-amount-in' : 'movements-amount-out'}>
                        {inflow - outflow >= 0 ? '+' : '–'}
                        {formatAmount(Math.abs(inflow - outflow).toFixed(8), currency)}
                      </span>
                    </div>
                  </div>
                ))}
          </div>
        </>
      )}

      {/* Table */}
      {isLoading ? (
        <LoadingState message={t('common:loading')} />
      ) : error ? (
        <div className="movements-error">{t('common:error')}</div>
      ) : rows.length === 0 ? (
        <EmptyState title="No movements" description="No cash movements found for this period." />
      ) : (
        <div className="movements-table-wrapper">
          <table className="movements-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Wallet</th>
                <th>Type</th>
                <th>Client</th>
                <th>Description</th>
                <th className="movements-amount-col">Amount</th>
                <th className="movements-actions-col"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row: WalletMovement) => {
                const amt = parseFloat(row.amount);
                const isIn = amt >= 0;
                return (
                  <tr key={row.id} className={isIn ? 'row-in' : 'row-out'}>
                    <td className="movements-cell-date">
                      <div className="movements-date-wrap">
                        <span className="movements-date-day">{formatDate(row.createdAt)}</span>
                        {row.createdAt && (
                          <span className="movements-date-time">{formatTime(row.createdAt)}</span>
                        )}
                      </div>
                    </td>
                    <td className="movements-cell-wallet">
                      <span className="movements-wallet-name">{row.walletName}</span>
                      <span className="movements-wallet-type">{row.walletType}</span>
                    </td>
                    <td>
                      <span className={`movements-type-badge type-${row.txType || 'debt'} ${row.isDebtPayment ? 'type-debt-payment' : ''}`}>
                        {txTypeLabel(row.txType, row.isDebtPayment)}
                      </span>
                    </td>
                    <td className="movements-cell-client">{row.clientName || '–'}</td>
                    <td className="movements-cell-desc">{row.description || '–'}</td>
                    <td className="movements-cell-amount">
                      <span className={isIn ? 'movements-amount-in' : 'movements-amount-out'}>
                        {isIn ? '+' : '–'}{formatAmount(Math.abs(amt).toFixed(8), row.currency)} {row.currency}
                      </span>
                    </td>
                    <td className="movements-cell-actions">
                      <div className="movements-actions-group">
                        {row.transactionId && (
                          <Link
                            to={`/transactions/${row.transactionId}`}
                            className="movements-tx-btn"
                            title="Открыть транзакцию"
                            onClick={(e) => e.stopPropagation()}
                          >
                            ↗
                          </Link>
                        )}
                        <button
                          className="movements-edit-btn"
                          title="Редактировать движение"
                          onClick={(e) => { e.stopPropagation(); openEdit(row); }}
                        >
                          ✏
                        </button>
                        {isAdmin && (
                          <button
                            className="movements-edit-btn"
                            title="Удалить движение"
                            onClick={(e) => { e.stopPropagation(); setDeletingMovementId(row.id); }}
                          >
                            -
                          </button>
                        )}
                        {isAdmin && (
                          <button
                            className="movements-edit-btn"
                            title="console log Raw"
                            onClick={(e) => { e.stopPropagation(); console.debug(JSON.stringify(row, null, 5)) }}
                          >
                            j
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Edit Modal */}
      {editingMovement && (
        <div className="mov-modal-overlay" onClick={() => setEditingMovement(null)}>
          <div className="mov-modal" onClick={(e) => e.stopPropagation()}>
            <div className="mov-modal-header">
              <h3 className="mov-modal-title">Редактировать движение</h3>
              <button className="mov-modal-close" onClick={() => setEditingMovement(null)}>✕</button>
            </div>
            <div className="mov-modal-body">
              <div className="mov-modal-field">
                <label className="mov-modal-label" htmlFor="edit-wallet">Кошелёк</label>
                <select
                  id="edit-wallet"
                  className="mov-modal-input"
                  value={editWalletId}
                  onChange={(e) => {
                    setEditWalletId(e.target.value);
                    const w = wallets.find(w => w.id === e.target.value);
                    if (w) setEditCurrency(w.currencyCode);
                  }}
                >
                  <option value="">— выбрать —</option>
                  {wallets.filter(w => w.isActive).map(w => (
                    <option key={w.id} value={w.id}>{w.name} ({w.currencyCode})</option>
                  ))}
                </select>
              </div>
              <div className="mov-modal-field">
                <label className="mov-modal-label" htmlFor="edit-currency">Валюта</label>
                <input
                  id="edit-currency"
                  type="text"
                  className="mov-modal-input"
                  value={editCurrency}
                  readOnly
                  style={{ background: 'var(--color-surface-hover)', cursor: 'default' }}
                />
              </div>
              <div className="mov-modal-field">
                <label className="mov-modal-label" htmlFor="edit-created-at">Дата и время (created_at)</label>
                <input
                  id="edit-created-at"
                  type="datetime-local"
                  className="mov-modal-input"
                  value={editCreatedAt}
                  onChange={(e) => setEditCreatedAt(e.target.value)}
                />
              </div>
              <div className="mov-modal-field">
                <label className="mov-modal-label" htmlFor="edit-amount">Сумма</label>
                <input
                  id="edit-amount"
                  type="number"
                  step="any"
                  className="mov-modal-input"
                  value={editAmount}
                  onChange={(e) => setEditAmount(e.target.value)}
                />
                <span className="mov-modal-hint">Введите сумму с нужным знаком (+/–)</span>
              </div>
              <div className="mov-modal-field">
                <label className="mov-modal-label">Тип движения</label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    className={`mov-dir-btn ${!editIsDebtPayment ? 'active-in' : ''}`}
                    onClick={() => setEditIsDebtPayment(false)}
                    type="button"
                  >
                    Regular
                  </button>
                  <button
                    className={`mov-dir-btn ${editIsDebtPayment ? 'active-out' : ''}`}
                    onClick={() => setEditIsDebtPayment(true)}
                    type="button"
                  >
                    Debt Payment
                  </button>
                </div>
              </div>
              {isAdmin && (
                <div className="mov-modal-field">
                  <label className="mov-modal-label" htmlFor="edit-tx-id">Привязка к транзакции (UUID)</label>
                  <input
                    id="edit-tx-id"
                    type="text"
                    className="mov-modal-input"
                    value={editTxId}
                    onChange={(e) => setEditTxId(e.target.value)}
                    placeholder="Оставьте пустым для отвязки"
                  />
                  <span className="mov-modal-hint">Вставьте UUID транзакции или очистите для отвязки</span>
                </div>
              )}
              {editError && <div className="mov-modal-error">{editError}</div>}
            </div>
            <div className="mov-modal-footer">
              <button
                className="mov-modal-btn mov-modal-btn-cancel"
                onClick={() => setEditingMovement(null)}
                disabled={updateMutation.isPending}
              >
                Отмена
              </button>
              <button
                className="mov-modal-btn mov-modal-btn-save"
                onClick={handleEditSave}
                disabled={updateMutation.isPending}
              >
                {updateMutation.isPending ? 'Сохранение…' : 'Сохранить'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      <ConfirmModal
        isOpen={!!deletingMovementId}
        onClose={() => setDeletingMovementId(null)}
        onConfirm={() => deletingMovementId && deleteMutation.mutate(deletingMovementId)}
        title="Удалить движение?"
        message="Это действие необратимо. Баланс кошелька будет пересчитан."
        confirmText="Удалить"
        confirmVariant="danger"
        isLoading={deleteMutation.isPending}
      />

      {/* Create Modal */}
      {showCreateModal && (
        <div className="mov-modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="mov-modal" onClick={(e) => e.stopPropagation()}>
            <div className="mov-modal-header">
              <h3 className="mov-modal-title">Создать движение</h3>
              <button className="mov-modal-close" onClick={() => setShowCreateModal(false)}>✕</button>
            </div>
            <div className="mov-modal-body">
              <div className="mov-modal-field">
                <label className="mov-modal-label" htmlFor="create-wallet">Кошелёк</label>
                <select
                  id="create-wallet"
                  className="mov-modal-input"
                  value={createWalletId}
                  onChange={(e) => {
                    setCreateWalletId(e.target.value);
                    const w = wallets.find(w => w.id === e.target.value);
                    if (w) setCreateCurrency(w.currencyCode);
                  }}
                >
                  <option value="">Выберите кошелёк...</option>
                  {wallets.filter(w => w.isActive).map(w => (
                    <option key={w.id} value={w.id}>{w.name} ({w.currencyCode})</option>
                  ))}
                </select>
              </div>
              <div className="mov-modal-field">
                <label className="mov-modal-label">Направление</label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    className={`mov-dir-btn ${createDirection === 'in' ? 'active-in' : ''}`}
                    onClick={() => setCreateDirection('in')}
                    type="button"
                  >
                    + IN (приход)
                  </button>
                  <button
                    className={`mov-dir-btn ${createDirection === 'out' ? 'active-out' : ''}`}
                    onClick={() => setCreateDirection('out')}
                    type="button"
                  >
                    − OUT (расход)
                  </button>
                </div>
              </div>
              <div className="mov-modal-field">
                <label className="mov-modal-label" htmlFor="create-amount">Сумма</label>
                <input
                  id="create-amount"
                  type="number"
                  min="0"
                  step="any"
                  className="mov-modal-input"
                  value={createAmount}
                  onChange={(e) => setCreateAmount(e.target.value)}
                />
              </div>
              <div className="mov-modal-field">
                <label className="mov-modal-label" htmlFor="create-currency">Валюта</label>
                <input
                  id="create-currency"
                  type="text"
                  className="mov-modal-input"
                  value={createCurrency}
                  onChange={(e) => setCreateCurrency(e.target.value)}
                  placeholder="EUR, USDT, ..."
                />
              </div>
              <div className="mov-modal-field">
                <label className="mov-modal-label" htmlFor="create-tx-id">Транзакция (UUID, опционально)</label>
                <input
                  id="create-tx-id"
                  type="text"
                  className="mov-modal-input"
                  value={createTxId}
                  onChange={(e) => setCreateTxId(e.target.value)}
                  placeholder="Оставьте пустым для standalone"
                />
              </div>
              {createError && <div className="mov-modal-error">{createError}</div>}
            </div>
            <div className="mov-modal-footer">
              <button
                className="mov-modal-btn mov-modal-btn-cancel"
                onClick={() => setShowCreateModal(false)}
                disabled={createMutation.isPending}
              >
                Отмена
              </button>
              <button
                className="mov-modal-btn mov-modal-btn-save"
                onClick={handleCreateSave}
                disabled={createMutation.isPending}
              >
                {createMutation.isPending ? 'Создание…' : 'Создать'}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .movements-page {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .movements-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 16px;
          flex-wrap: wrap;
        }

        .movements-title {
          font-size: 22px;
          font-weight: 700;
          color: var(--color-text);
          margin: 0;
        }

        .movements-date-filter {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .movements-date-input {
          padding: 6px 10px;
          font-size: 13px;
          border: 1px solid var(--color-border);
          border-radius: 6px;
          background: var(--color-bg);
          color: var(--color-text);
        }

        .movements-date-sep {
          color: var(--color-text-muted);
          font-size: 14px;
        }

        .movements-today-btn {
          padding: 6px 14px;
          font-size: 13px;
          background: var(--color-surface);
          color: var(--color-text-muted);
          border: 1px solid var(--color-border);
          border-radius: 6px;
          cursor: pointer;
          font-weight: 500;
          transition: background 0.15s, color 0.15s, border-color 0.15s;
        }

        .movements-today-btn:hover {
          border-color: var(--color-primary);
          color: var(--color-primary);
        }

        .movements-today-btn.active {
          background: var(--color-primary);
          color: white;
          border-color: var(--color-primary);
        }

        .movements-my-only-btn {
          padding: 6px 14px;
          font-size: 13px;
          background: var(--color-surface);
          color: var(--color-text-muted);
          border: 1px solid var(--color-border);
          border-radius: 6px;
          cursor: pointer;
          font-weight: 500;
          transition: background 0.15s, color 0.15s, border-color 0.15s;
        }

        .movements-my-only-btn:hover {
          border-color: var(--color-primary);
          color: var(--color-primary);
        }

        .movements-my-only-btn.active {
          background: var(--color-primary);
          color: white;
          border-color: var(--color-primary);
        }

        /* Summary Toggle */
        .movements-summary-toggle {
          display: flex;
          gap: 0;
          margin-bottom: 4px;
        }

        .movements-summary-toggle-btn {
          padding: 5px 14px;
          font-size: 12px;
          font-weight: 600;
          border: 1px solid var(--color-border);
          background: var(--color-surface);
          color: var(--color-text-muted);
          cursor: pointer;
          transition: all 0.15s;
        }

        .movements-summary-toggle-btn:first-child {
          border-radius: 6px 0 0 6px;
        }

        .movements-summary-toggle-btn:last-child {
          border-radius: 0 6px 6px 0;
          border-left: none;
        }

        .movements-summary-toggle-btn.active {
          background: var(--color-primary);
          color: white;
          border-color: var(--color-primary);
        }

        /* Direction Filter */
        .movements-direction-filter {
          display: flex;
          gap: 0;
        }

        .movements-direction-btn {
          padding: 5px 10px;
          font-size: 12px;
          font-weight: 600;
          border: 1px solid var(--color-border);
          background: var(--color-surface);
          color: var(--color-text-muted);
          cursor: pointer;
          transition: all 0.15s;
        }

        .movements-direction-btn:first-child {
          border-radius: 6px 0 0 6px;
        }

        .movements-direction-btn:last-child {
          border-radius: 0 6px 6px 0;
          border-left: none;
        }

        .movements-direction-btn:not(:first-child):not(:last-child) {
          border-left: none;
        }

        .movements-direction-btn.active {
          background: var(--color-primary);
          color: white;
          border-color: var(--color-primary);
        }

        .movements-direction-btn.in.active {
          background: var(--color-amount-in, #16a34a);
          border-color: var(--color-amount-in, #16a34a);
        }

        .movements-direction-btn.out.active {
          background: var(--color-amount-out, #dc2626);
          border-color: var(--color-amount-out, #dc2626);
        }

        /* Summary Cards */
        .movements-summary {
          display: flex;
          gap: 12px;
          flex-wrap: wrap;
        }

        .movements-summary-card {
          background: var(--color-surface);
          border: 1px solid var(--color-border);
          border-radius: 10px;
          padding: 14px 18px;
          min-width: 160px;
          display: flex;
          flex-direction: column;
          gap: 6px;
        }

        .movements-summary-currency {
          font-size: 16px;
          font-weight: 700;
          color: var(--color-text);
          margin-bottom: 4px;
        }

        .movements-summary-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 12px;
          font-size: 13px;
        }

        .movements-summary-label {
          color: var(--color-text-muted);
        }

        .movements-summary-divider {
          height: 1px;
          background: var(--color-border);
          margin: 2px 0;
        }

        /* Amount colors */
        .movements-amount-in {
          color: var(--color-amount-in, #16a34a);
          font-weight: 600;
          font-family: monospace;
        }

        .movements-amount-out {
          color: var(--color-amount-out, #dc2626);
          font-weight: 600;
          font-family: monospace;
        }

        .movements-amount-link {
          text-decoration: none;
        }

        .movements-amount-link:hover {
          text-decoration: underline;
        }

        /* Table */
        .movements-table-wrapper {
          overflow-x: auto;
          border: 1px solid var(--color-border);
          border-radius: 8px;
        }

        .movements-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 13px;
          white-space: nowrap;
        }

        .movements-table thead {
          position: sticky;
          top: 0;
          z-index: 1;
        }

        .movements-table th {
          background: var(--color-table-header, var(--color-surface));
          border-bottom: 2px solid var(--color-border);
          padding: 10px 12px;
          text-align: left;
          font-weight: 600;
          font-size: 12px;
          color: var(--color-text-muted);
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .movements-table td {
          border-bottom: 1px solid var(--color-border);
          padding: 8px 12px;
          vertical-align: middle;
          color: var(--color-text);
        }

        .movements-table tbody tr:last-child td { border-bottom: none; }

        .movements-table tbody tr.row-in { background: var(--color-amount-in-bg, rgba(22,163,74,0.05)); }
        .movements-table tbody tr.row-out { background: var(--color-amount-out-bg, rgba(220,38,38,0.05)); }
        .movements-table tbody tr:hover { filter: brightness(0.97); }

        .movements-cell-date {
          font-family: monospace;
          font-size: 12px;
        }

        .movements-date-wrap {
          display: flex;
          flex-direction: column;
          gap: 1px;
        }

        .movements-date-day { font-weight: 500; }

        .movements-date-time {
          font-size: 11px;
          color: var(--color-text-muted);
        }

        .movements-cell-wallet {
          display: flex;
          flex-direction: column;
          gap: 1px;
        }

        .movements-wallet-name { font-weight: 500; }
        .movements-wallet-type {
          font-size: 11px;
          color: var(--color-text-muted);
          text-transform: capitalize;
        }

        .movements-cell-client { color: var(--color-text-muted); }

        .movements-cell-desc {
          max-width: 180px;
          overflow: hidden;
          text-overflow: ellipsis;
          color: var(--color-text-muted);
        }

        .movements-amount-col { text-align: right; }
        .movements-cell-amount { text-align: right; font-family: monospace; }


        .movements-actions-group {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 4px;
        }

        .movements-tx-btn,
        .movements-edit-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 28px;
          height: 28px;
          border-radius: 6px;
          background: var(--color-surface);
          border: 1px solid var(--color-border);
          font-size: 13px;
          text-decoration: none;
          transition: background 0.15s, border-color 0.15s, transform 0.1s;
          cursor: pointer;
          padding: 0;
          line-height: 1;
        }

        .movements-tx-btn { color: var(--color-primary); }
        .movements-tx-btn:hover {
          background: var(--color-primary);
          border-color: var(--color-primary);
          color: white;
          transform: scale(1.05);
        }

        .movements-edit-btn { color: var(--color-text-muted); }
        .movements-edit-btn:hover {
          background: var(--color-warning, #f59e0b);
          border-color: var(--color-warning, #f59e0b);
          color: white;
          transform: scale(1.05);
        }

        .movements-delete-btn { color: var(--ctp-red, #ef4444); }
        .movements-delete-btn:hover {
          background: var(--ctp-red, #ef4444);
          border-color: var(--ctp-red, #ef4444);
          color: white;
          transform: scale(1.05);
        }

        .movements-create-btn {
          padding: 6px 14px;
          border-radius: 6px;
          border: 1px solid var(--ctp-teal, #14b8a6);
          background: var(--ctp-teal, #14b8a6);
          color: white;
          font-size: 13px;
          font-weight: 600;
          cursor: pointer;
          transition: filter 0.15s;
        }
        .movements-create-btn:hover { filter: brightness(1.1); }

        .movements-actions-col { width: 110px; text-align: center; }
        .movements-cell-actions { text-align: center; width: 110px; }

        .mov-dir-btn {
          flex: 1;
          padding: 8px;
          font-size: 13px;
          font-weight: 600;
          border: 1px solid var(--color-border);
          border-radius: 7px;
          background: var(--color-surface);
          color: var(--color-text-muted);
          cursor: pointer;
          transition: all 0.15s;
        }
        .mov-dir-btn.active-in {
          background: rgba(16, 185, 129, 0.15);
          border-color: var(--ctp-green, #10b981);
          color: var(--ctp-green, #10b981);
        }
        .mov-dir-btn.active-out {
          background: rgba(239, 68, 68, 0.15);
          border-color: var(--ctp-red, #ef4444);
          color: var(--ctp-red, #ef4444);
        }

        /* Edit Modal */
        .mov-modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0,0,0,0.4);
          z-index: 1000;
          display: flex;
          align-items: center;
          justify-content: center;
          backdrop-filter: blur(2px);
        }

        .mov-modal {
          background: var(--color-bg);
          border: 1px solid var(--color-border);
          border-radius: 12px;
          box-shadow: 0 20px 60px rgba(0,0,0,0.3);
          width: 100%;
          max-width: 400px;
          display: flex;
          flex-direction: column;
          overflow: hidden;
        }

        .mov-modal-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 16px 20px;
          border-bottom: 1px solid var(--color-border);
        }

        .mov-modal-title {
          margin: 0;
          font-size: 16px;
          font-weight: 700;
          color: var(--color-text);
        }

        .mov-modal-close {
          background: none;
          border: none;
          font-size: 16px;
          cursor: pointer;
          color: var(--color-text-muted);
          padding: 2px 6px;
          border-radius: 4px;
          transition: color 0.15s, background 0.15s;
        }
        .mov-modal-close:hover { background: var(--color-surface); color: var(--color-text); }

        .mov-modal-body {
          padding: 20px;
          display: flex;
          flex-direction: column;
          gap: 14px;
        }

        .mov-modal-info {
          display: flex;
          gap: 8px;
          font-size: 13px;
        }
        .mov-modal-info-label { color: var(--color-text-muted); min-width: 70px; }
        .mov-modal-info-value { font-weight: 600; color: var(--color-text); }

        .mov-modal-field {
          display: flex;
          flex-direction: column;
          gap: 5px;
        }

        .mov-modal-label {
          font-size: 12px;
          font-weight: 600;
          color: var(--color-text-muted);
          text-transform: uppercase;
          letter-spacing: 0.4px;
        }

        .mov-modal-input {
          padding: 8px 12px;
          font-size: 14px;
          border: 1px solid var(--color-border);
          border-radius: 7px;
          background: var(--color-surface);
          color: var(--color-text);
          width: 100%;
          box-sizing: border-box;
          transition: border-color 0.15s;
        }
        .mov-modal-input:focus {
          outline: none;
          border-color: var(--color-primary);
        }

        .mov-modal-hint {
          font-size: 11px;
          color: var(--color-text-muted);
        }

        .mov-modal-check-label {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 13px;
          color: var(--color-text);
          cursor: pointer;
        }

        .mov-modal-error {
          font-size: 13px;
          color: var(--color-error, #dc2626);
          background: rgba(220,38,38,0.08);
          border-radius: 6px;
          padding: 8px 12px;
        }

        .mov-modal-footer {
          display: flex;
          gap: 10px;
          justify-content: flex-end;
          padding: 14px 20px;
          border-top: 1px solid var(--color-border);
        }

        .mov-modal-btn {
          padding: 8px 20px;
          font-size: 14px;
          font-weight: 600;
          border-radius: 7px;
          cursor: pointer;
          border: 1px solid transparent;
          transition: background 0.15s, color 0.15s, border-color 0.15s;
        }
        .mov-modal-btn:disabled { opacity: 0.6; cursor: not-allowed; }

        .mov-modal-btn-cancel {
          background: var(--color-surface);
          color: var(--color-text-muted);
          border-color: var(--color-border);
        }
        .mov-modal-btn-cancel:hover:not(:disabled) {
          color: var(--color-text);
          border-color: var(--color-text-muted);
        }

        .mov-modal-btn-save {
          background: var(--color-primary);
          color: white;
        }
        .mov-modal-btn-save:hover:not(:disabled) {
          filter: brightness(1.1);
        }

        /* Type badges */
        .movements-type-badge {
          display: inline-block;
          padding: 2px 8px;
          border-radius: 4px;
          font-size: 11px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.3px;
        }

        .type-exchange { background: var(--badge-exchange-bg, #dbeafe); color: var(--badge-exchange-text, #1d4ed8); }
        .type-deposit { background: var(--badge-deposit-bg, #dcfce7); color: var(--badge-deposit-text, #15803d); }
        .type-withdrawal { background: var(--badge-withdrawal-bg, #fef9c3); color: var(--badge-withdrawal-text, #a16207); }
        .type-expense { background: var(--badge-expense-bg, #fee2e2); color: var(--badge-expense-text, #b91c1c); }
        .type-transfer { background: var(--badge-transfer-bg, #f3e8ff); color: var(--badge-transfer-text, #7e22ce); }
        .type-debt-payment { background: #fce7f3; color: #9d174d; }

        .movements-error {
          color: var(--color-error, #dc2626);
          padding: 16px;
          text-align: center;
        }
      `}</style>
    </div>
  );
}
